import { aiClient } from '../sdk/aiClient';
import { toast } from '../utils/toast';

// Configuration for different content types and their AI providers
const contentTypeConfig = {
  grantWriter: {
    model: 'claude-3-opus-20240229',
    temperature: 0.7,
    provider: 'anthropic',
    maxTokens: 4096
  },
  sqlWriter: {
    model: 'gpt-4-turbo-preview',
    temperature: 0.3,
    provider: 'openai',
    maxTokens: 4096
  },
  pySparkWriter: {
    model: 'gpt-4-turbo-preview',
    temperature: 0.3,
    provider: 'openai',
    maxTokens: 4096
  },
  lessonPlanCreator: {
    model: 'claude-3-opus-20240229',
    temperature: 0.7,
    provider: 'anthropic',
    maxTokens: 4096
  },
  courseOutlineCreator: {
    model: 'claude-3-opus-20240229',
    temperature: 0.7,
    provider: 'anthropic',
    maxTokens: 4096
  },
  trainingManualCreator: {
    model: 'claude-3-opus-20240229',
    temperature: 0.7,
    provider: 'anthropic',
    maxTokens: 4096
  },
  researchAssistant: {
    model: 'claude-3-opus-20240229',
    temperature: 0.5,
    provider: 'anthropic',
    maxTokens: 4096
  },
  contentPolisher: {
    model: 'claude-3-opus-20240229',
    temperature: 0.4,
    provider: 'anthropic',
    maxTokens: 4096
  },
  connectionTest: {
    model: 'claude-3-opus-20240229',
    temperature: 0.7,
    provider: 'anthropic',
    maxTokens: 10
  },
  playFormatter: {
    model: 'gemini-1.5-pro',
    temperature: 0.3,
    provider: 'gemini',
    maxTokens: 4096
  },
  default: {
    model: process.env.REACT_APP_DEFAULT_MODEL || 'claude-3-opus-20240229',
    temperature: 0.7,
    provider: process.env.REACT_APP_DEFAULT_AI_PROVIDER || 'anthropic',
    maxTokens: 4096
  }
};

/**
 * Service for handling AI chat interactions
 */
class AIChatService {
  constructor() {
    this.client = aiClient;
    this.botPersonalities = {
      contentSynthesizer: {
        name: 'Content Synthesizer',
        description: 'Transform ideas into polished professional communications',
        icon: '📊',
        welcomeMessage: 'Welcome to Content Synthesizer! I\'m here to help you transform your ideas into polished professional communications. What would you like to create today?',
        systemPrompt: 'You are Content Synthesizer, a professional communication AI assistant. Help users create polished business content, presentations, reports, and other professional communications. Focus on clarity, impact, and maintaining a professional tone. Provide strategic guidance on content structure, messaging, and delivery. Your goal is to help users communicate effectively in professional settings.'
      },
      professionalDevelopment: {
        name: 'Professional Development Coach',
        description: 'Data-driven guidance for accelerating your career growth',
        icon: '📈',
        welcomeMessage: 'Welcome! I\'m your Professional Development Coach, ready to help you accelerate your career growth with data-driven guidance. What area of professional development would you like to focus on?',
        systemPrompt: 'You are a Professional Development Coach AI assistant, specialized in career advancement and workplace effectiveness. Help users develop leadership skills, create strategic plans, improve team management, and enhance professional capabilities. Provide actionable insights based on industry best practices and data-driven approaches. Your goal is to help users achieve their career objectives while maintaining a professional and strategic focus.'
      }
    };
  }

  /**
   * Get bot configuration by type
   * @param {string} botType - Type of bot
   * @returns {Object} Bot configuration
   */
  getBotConfig(botType) {
    return this.botPersonalities[botType];
  }

  /**
   * Generate a response from the AI
   * @param {string} prompt - User message
   * @param {Array} context - Chat history
   * @param {string} contentType - Type of content
   * @returns {Promise<Object>} AI response
   */
  async generateResponse(prompt, context = [], contentType = 'default') {
    const config = contentTypeConfig[contentType] || contentTypeConfig.default;
    
    try {
      let response;
      
      switch (config.provider) {
        case 'anthropic':
          response = await this.client.generateWithAnthropic(prompt, {
            model: config.model,
            temperature: config.temperature,
            maxTokens: config.maxTokens
          });
          break;
          
        case 'openai':
          response = await this.client.generateWithOpenAI(prompt, {
            model: config.model,
            temperature: config.temperature,
            maxTokens: config.maxTokens
          });
          break;
          
        case 'gemini':
          response = await this.client.generateWithGemini(prompt, {
            model: config.model,
            temperature: config.temperature
          });
          break;
          
        default:
          throw new Error(`Unsupported AI provider: ${config.provider}`);
      }

      return {
        content: response,
        type: contentType,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('AI Service Error:', error);
      throw new Error(`Failed to generate content: ${error.message}`);
    }
  }

  async checkAvailability() {
    return await this.client.checkAvailability();
  }
}

// Export singleton instance
export const aiChatService = new AIChatService(); 